import {
  Box,
  Center,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  type InputProps,
  Popover,
  PopoverAnchor,
  PopoverContent,
  Portal,
  Text,
  useDisclosure,
  useOutsideClick,
  VStack,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { Icon } from "@src/components/ui-kit/Icon";
import { LoadingOverlay } from "@src/components/ui-kit/LoadingOverlay/LoadingOverlay";
import { WrapComponent } from "@src/utils/components/WrapComponent";
import { observer } from "mobx-react-lite";
import type React from "react";
import { type FocusEvent, useRef } from "react";

type Option = {
  label: React.ReactNode;
  value: string;
};

export interface AutocompleteFieldProps {
  autoCompleteOptions: Option[];
  onChange: (value: string) => void;
  onSelect: (option: Option) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement, Element>) => void;
  charCount: number;
  value: string;
  label?: string;
  loading: boolean;
  error?: string;
  isDisabled?: boolean;
  placeholder?: string;
  showPopoverTrigger?: boolean;
  onPopoverOpen?: () => void;
  autoComplete?: InputProps["autoComplete"];
  noOptionsMessage?: string;
}

export const AutocompleteField = observer(function AutocompleteField({
  onBlur,
  autoCompleteOptions,
  value,
  onChange,
  label,
  charCount,
  onSelect,
  loading,
  error,
  isDisabled,
  placeholder,
  showPopoverTrigger = false,
  onPopoverOpen,
  autoComplete = "off",
  noOptionsMessage,
}: AutocompleteFieldProps) {
  const initialRef = useRef(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const popoverContentRef = useRef(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
    if (e.target.value.length >= charCount) {
      if (isDisabled) return;
      onOpen();
      onPopoverOpen?.();
    } else {
      onClose();
    }
  };

  const handleSelect = (option: Option) => {
    onSelect(option);
    onClose();
  };

  const handleOnOpen = () => {
    inputRef.current?.focus();
    onOpen();
    if (!isOpen) return;
    onPopoverOpen?.();
  };

  useOutsideClick({
    ref: popoverContentRef,
    handler: onClose,
    enabled: isOpen,
  });

  return (
    <Popover
      initialFocusRef={initialRef}
      isOpen={isOpen}
      onClose={onClose}
      placement="bottom-end"
    >
      <PopoverAnchor>
        <Box ref={initialRef}>
          <FormControl isDisabled={isDisabled} isInvalid={!!error}>
            {label && <FormLabel htmlFor="email">{label}</FormLabel>}
            <WrapComponent
              if={showPopoverTrigger}
              with={(children) => (
                <HStack spacing="0">
                  {children}
                  <IconButton
                    aria-label={t`Toggle popover`}
                    icon={<Icon name="chevron-down" />}
                    onClick={handleOnOpen}
                    roundedLeft="none"
                  />
                </HStack>
              )}
            >
              <Input
                ref={inputRef}
                autoComplete={autoComplete}
                onBlur={onBlur}
                onChange={handleChange}
                placeholder={placeholder}
                roundedRight={showPopoverTrigger ? "none" : undefined}
                value={value}
              />
            </WrapComponent>
            {!!error && <FormErrorMessage>{error}</FormErrorMessage>}
          </FormControl>
        </Box>
      </PopoverAnchor>

      <Portal>
        <PopoverContent
          ref={popoverContentRef}
          pos="relative"
          overflow="hidden"
          w="max-content"
          minH="fit-content"
          maxH="56"
          p="0"
          color="black"
          border="none"
          shadow="xl!"
          outline="none"
          rounded="lg"
        >
          <LoadingOverlay
            isLoading={loading}
            minH="48"
            minW="48"
            size="md"
            position="absolute"
            overflow="auto"
          >
            {autoCompleteOptions.length > 0 ? (
              <VStack align="start" spacing="0">
                {autoCompleteOptions.map((option) => (
                  <Box
                    key={option.value}
                    w="full"
                    _hover={{
                      bg: "gray.100",
                      cursor: "pointer",
                    }}
                    onClick={() => handleSelect(option)}
                    paddingBlock="2"
                    paddingInline="4"
                  >
                    {option.label}
                  </Box>
                ))}
              </VStack>
            ) : (
              <Center w="full" h="48" px="4">
                <Text color="gray.500">
                  {noOptionsMessage ?? <Trans>No options</Trans>}
                </Text>
              </Center>
            )}
          </LoadingOverlay>
        </PopoverContent>
      </Portal>
    </Popover>
  );
});
